import React from "react";
import { IconButton, FormControl } from "@mui/material";
import { Mic, Stop } from "@mui/icons-material";

/**
 * AudioRecorder
 *
 * - Single button to start/stop recording from the user's mic.
 * - On stop, calls `handleData(contentType, fileExtension, dataBytes)`.
 * - Tries multiple MIME types (incl. Safari-friendly audio/mp4) and uses the first supported.
 * - Assembles chunks after the final 'stop' event to avoid partial data.
 * - Adds a maxDurationMs auto-stop with a cleared timeout if stopped early/unmounted.
 * - Cleans up tracks and event listeners on stop/unmount.
 */

function pickSupportedMimeType(candidates) {
  const fallbacks = [
    "audio/mp3",
    "audio/ogg",
    "audio/wav",
    "audio/mp4", // Safari/iOS/macOS often prefers MP4/M4A
  ];
  const list = [...(candidates || []), ...fallbacks];
  for (const type of list) {
    if (window.MediaRecorder?.isTypeSupported?.(type)) return type;
  }
  // If nothing explicitly supported, let the UA choose.
  return undefined;
}

function extFromContentType(ct) {
  if (!ct) return "bin";
  const t = ct.toLowerCase();
  if (t.includes("ogg")) return "ogg";
  if (t.includes("mp3")) return "mp3";
  if (t.includes("mp4") || t.includes("m4a")) return "m4a";
  if (t.includes("wav")) return "wav";
  return "bin";
}

export const AudioRecorder = ({
  handleData,
  constraints,
  preferredMimeTypes,
  timesliceMs = null,
  className,
  onStart = () => {},
  onError = () => {},
  maxDurationMs = 60 * 60 * 1000,
}) => {
  const [isRecording, setIsRecording] = React.useState(false);

  const recorderRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timeoutRef = React.useRef(null);

  // Stable handler: push non-empty chunks
  const onDataAvailable = React.useCallback(async (e) => {
    if (e?.data && e.data.size > 0) {
      chunksRef.current.push(e.data);
      // Optional: debug the bytes of this chunk
      // console.log(await e.data.bytes());
    }
  }, []);

  // Cleanup shared resources & listeners
  const cleanup = React.useCallback(() => {
    // Clear auto-stop timer (if any)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Detach listeners
    try {
      if (recorderRef.current) {
        recorderRef.current.removeEventListener("dataavailable", onDataAvailable);
        recorderRef.current.onstop = null; // we attach stop via property below
      }
    } catch {}

    // Release recorder
    recorderRef.current = null;

    // Stop & release tracks
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop();
        } catch {}
      }
      streamRef.current = null;
    }

    // Reset chunks
    chunksRef.current = [];
  }, [onDataAvailable]);

  // Assemble & deliver on final stop
  const onRecorderStop = React.useCallback(async () => {
    try {
      const chunks = chunksRef.current;
      const chosenMime = (recorderRef.current?.mimeType || chunks[0]?.type || "").trim();
      const blob = new Blob(chunks, { type: chosenMime || undefined });
      const buf = await blob.arrayBuffer();
      const bytes = new Uint8Array(buf);
      const ext = extFromContentType(blob.type);

      await handleData(blob.type || "application/octet-stream", ext, bytes);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      onError(e);
      console.error(e);
    } finally {
      setIsRecording(false);
      cleanup();
    }
  }, [cleanup, handleData, onError]);

  const start = React.useCallback(async () => {
    if (!window.MediaRecorder) {
      const e = new Error("MediaRecorder API is not supported in this browser.");
      onError(e);
      throw e;
    }

    onStart();

    try {
      const userConstraints = constraints || { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia({
        ...userConstraints,
        audio: userConstraints?.audio ?? true,
        video: false, // ensure we don't request video by accident
      });

      const mimeType = pickSupportedMimeType(preferredMimeTypes);
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      // Store refs BEFORE wiring events
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];

      // Wire listeners with stable references
      recorder.addEventListener("dataavailable", onDataAvailable);
      recorder.onstop = onRecorderStop;

      // Start recording (with optional timeslice)
      if (typeof timesliceMs === "number" && timesliceMs > 0) {
        recorder.start(timesliceMs);
      } else {
        recorder.start();
      }

      setIsRecording(true);

      // Auto-stop after maxDurationMs
      if (maxDurationMs && maxDurationMs > 0) {
        timeoutRef.current = setTimeout(() => {
          if (recorderRef.current?.state === "recording") {
            // request a final data chunk, then stop
            try {
              recorderRef.current.requestData();
            } catch {}
            recorderRef.current.stop();
          }
        }, maxDurationMs);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      onError(e);
      console.error(e);
      setIsRecording(false);
      cleanup();
    }
  }, [constraints, preferredMimeTypes, timesliceMs, maxDurationMs, onStart, onError, onDataAvailable, onRecorderStop, cleanup]);

  const stop = React.useCallback(() => {
    // Clear pending auto-stop if user stops early
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.requestData();
        } catch {}
        recorderRef.current.stop();
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      onError(e);
      console.error(e);
      setIsRecording(false);
      cleanup();
    }
  }, [cleanup, onError]);

  // Stop recording if the component unmounts while active
  React.useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch {}
      cleanup();
    };
  }, [cleanup]);

  // Optional: stop if the tab becomes hidden (prevents silent background recording)
  React.useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && recorderRef.current?.state === "recording") {
        stop();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [stop]);

  return (
    <FormControl>
      <IconButton
        className={className}
        color="primary"
        onClick={() => (isRecording ? stop() : start())}
        aria-pressed={isRecording}
      >
        {isRecording ? <Stop /> : <Mic />}
      </IconButton>
    </FormControl>
  );
};

export default AudioRecorder;
